import * as React from 'react';
import PropType from 'prop-types';

import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';
import Checkbox from '@mui/material/Checkbox';
import { InputAdornment, ListSubheader, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

function NDropdown(props) {
  const { value, label, onChange, options, id, multiple, onBlur, onClose, selectAll, searchable } = props;

  const [searchText, setSearchText] = React.useState('');

  const ITEM_HEIGHT = 80;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      },
    },
    autoFocus: false,
  };

  const filteredOptions = React.useMemo(() => {
    if (!searchable || !searchText) return options;
    return options?.filter((opt) =>
      opt.label?.toString().toLowerCase().includes(searchText.toLowerCase())
    );
  }, [options, searchText, searchable]);

  const isAllSelected = React.useMemo(() => {
    const targetOptions = searchText ? filteredOptions : options;
    return targetOptions?.length > 0 && Array.isArray(value) && targetOptions.every((opt) => value.includes(opt.value));
  }, [options, filteredOptions, value, searchText]);

  const handleChange = (event) => {
    const {
      target: { value: selectedValue },
    } = event;

    if (selectedValue.includes('select-all')) {
      let newValue = [];
      if (searchText) {
        const filteredValues = filteredOptions.map(o => o.value);
        if (isAllSelected) {
          // Deselect filtered options
          newValue = value.filter(v => !filteredValues.includes(v));
        } else {
          // Select filtered options (union)
          newValue = [...new Set([...value, ...filteredValues])];
        }
      } else {
        newValue = isAllSelected ? [] : options.map((opt) => opt.value);
      }

      onChange({
        target: {
          value: newValue,
          name: props.name,
        },
      });
      return;
    }

    onChange(event);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key !== 'Escape') {
      e.stopPropagation();
    }
  };

  return (
    <div className='App-inputs input-dropdown'>
      <FormControl fullWidth error={props?.error}>
        <InputLabel id={`n-dropdown-select-label${label}`}>{label}</InputLabel>
        <Select
          {...props}
          labelId={`n-dropdown-select-label${label}`}
          id={id}
          value={value}
          label={label}
          onChange={selectAll && multiple ? handleChange : onChange}
          multiple={multiple}
          MenuProps={MenuProps}
          onBlur={onBlur}
          onClose={(e) => {
            setSearchText('');
            onClose && onClose(e);
          }}
        >
          {searchable && (
            <ListSubheader>
              <TextField
                size="small"
                autoFocus
                placeholder="Type to search..."
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onClick={(e) => e.stopPropagation()} // Prevent select from closing
                value={searchText}
              />
            </ListSubheader>
          )}

          {selectAll && multiple && options?.length > 0 && (
            <MenuItem value='select-all'>
              <Checkbox
                checked={isAllSelected}
              />
              {searchText
                ? `Select Matching (${filteredOptions?.length})`
                : `Select All (${options?.length})`
              }
            </MenuItem>
          )}
          {filteredOptions?.length ? filteredOptions?.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {multiple ? (
                <>
                  <Checkbox checked={value.indexOf(o.value) > -1} />
                  {o.label}
                </>
              ) : (
                o.label
              )}
            </MenuItem>
          )) : <MenuItem disabled value="">No options available</MenuItem>}
        </Select>
        {props?.helperText && <FormHelperText>{props?.helperText}</FormHelperText>}
      </FormControl>
    </div>
  );
}

NDropdown.propTypes = {
  id: PropType.string,
  error: PropType.bool,
  multiple: PropType.bool,
  selectAll: PropType.bool,
  searchable: PropType.bool,
  value: PropType.oneOfType([PropType.string, PropType.number, PropType.array]),
  label: PropType.string,
  helperText: PropType.string,
  onChange: PropType.func,
  onBlur: PropType.func,
  onClose: PropType.func,
  options: PropType.arrayOf(
    PropType.shape({
      label: PropType.oneOfType([PropType.string, PropType.number]),
      value: PropType.oneOfType([PropType.string, PropType.number]),
    }),
  ),
  name: PropType.string,
};

NDropdown.defaultProps = {
  error: false,
  multiple: false,
  selectAll: false,
  searchable: false,
  value: '',
  id: '',
  label: '',
  helperText: '',
  onChange: () => { },
  onBlur: () => { },
  onClose: () => { },
  options: undefined,
};

export default NDropdown;
